import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Camera, Globe, Users, ArrowRight, Sparkles, Heart, Menu, X, Plus, User, Crown, Shield, LogOut, Trophy, BookOpen, PawPrint, Mail } from 'lucide-react';
import { MapFilterProvider } from '../contexts/MapFilterContext';
import { InteractiveMap } from './InteractiveMap';
import { FeaturedStories } from './FeaturedStories';
import { Footer } from './Footer';
import { useAuth, LoginForm, UserProfile } from './UserAuth';
import { StoryFlipbook } from './StoryFlipbook';
import { ClubShowcase } from './ClubShowcase';
import api from '../services/api';

// Curi the Kitten hero images - adventure scenes
const CURI_IMAGES = [
  {
    url: '/curi-images/Upscale_0047.jpg',
    title: 'Curi the Explorer',
    caption: 'A curious kitten ready for adventure'
  },
  {
    url: '/curi-images/PhotoBasedArtwork_0044.jpg',
    title: 'Julia and Curi',
    caption: 'Best friends on a grand adventure'
  },
  {
    url: '/curi-images/Upscale_0199.jpg',
    title: 'Exploring the World',
    caption: 'Sending messages home from faraway places'
  }
];

// Curi Header with Navigation - Warm, playful theme
const CuriHeader = ({ clubName, tagline }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const { user, logout } = useAuth();
  const [userPoints, setUserPoints] = useState(0);

  useEffect(() => {
    if (user?.email) {
      loadUserPoints();
    }
  }, [user]);

  const loadUserPoints = async () => {
    try {
      const data = await api.getUserPoints(user.email);
      setUserPoints(data.totalPoints || 0);
    } catch (error) {
      console.error('Error loading points:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 shadow-lg border-b border-orange-500" style={{ zIndex: 1000 }}>
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - Curi branding */}
            <button
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none bg-transparent border-0"
              onClick={() => navigate('/club/curi')}
              title="Curi the Kitten Home"
              type="button"
              data-testid="curi-logo"
            >
              <span className="text-2xl sm:text-3xl">🐱</span>
              <div className="flex flex-col">
                <span className="text-lg sm:text-xl font-bold text-white leading-tight drop-shadow-md">{clubName}</span>
                <span className="text-[8px] sm:text-[10px] text-orange-100 -mt-0.5">{tagline}</span>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => scrollToSection('story')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                The Story
              </button>
              <button 
                onClick={() => scrollToSection('flipbook')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Storybook
              </button>
              <button 
                onClick={() => scrollToSection('map')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Adventure Map
              </button>
              <button 
                onClick={() => scrollToSection('stories')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Messages Home
              </button>
              <button 
                onClick={() => navigate('/store?theme=curi')} 
                className="text-white/90 hover:text-white transition-colors font-medium"
              >
                Shop
              </button>
              
              {/* User Section */}
              {user ? (
                <div className="flex items-center space-x-3">
                  <Badge className="bg-white/20 text-white border-white/30">
                    <Trophy className="w-3 h-3 mr-1" />
                    {userPoints} pts
                  </Badge>
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="flex items-center space-x-2 text-white/90 hover:text-white"
                  >
                    <User className="w-5 h-5" />
                    <span className="text-sm">{user.name || user.email.split('@')[0]}</span>
                  </button>
                </div>
              ) : (
                <Button 
                  onClick={() => setShowLoginForm(true)}
                  className="bg-white text-orange-600 hover:bg-orange-50"
                  size="sm"
                >
                  Login
                </Button>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-orange-500/95 backdrop-blur-sm border-t border-orange-400">
            <nav className="container mx-auto px-4 py-4 flex flex-col space-y-3">
              <button onClick={() => scrollToSection('story')} className="text-white text-left py-2">
                The Story
              </button>
              <button onClick={() => scrollToSection('flipbook')} className="text-white text-left py-2">
                Storybook
              </button>
              <button onClick={() => scrollToSection('map')} className="text-white text-left py-2">
                Adventure Map
              </button>
              <button onClick={() => scrollToSection('stories')} className="text-white text-left py-2">
                Messages Home
              </button>
              <button onClick={() => { navigate('/store?theme=curi'); setIsMenuOpen(false); }} className="text-white text-left py-2">
                Shop
              </button>
              {user ? (
                <>
                  <div className="flex items-center space-x-2 text-white py-2">
                    <Trophy className="w-4 h-4" />
                    <span>{userPoints} points</span>
                  </div>
                  <button onClick={() => { setShowUserProfile(true); setIsMenuOpen(false); }} className="text-white text-left py-2">
                    My Profile
                  </button>
                </>
              ) : (
                <Button 
                  onClick={() => { setShowLoginForm(true); setIsMenuOpen(false); }}
                  className="bg-white text-orange-600 hover:bg-orange-50 w-full"
                >
                  Login
                </Button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Login Form Modal */}
      {showLoginForm && (
        <div style={{ zIndex: 1100, position: 'relative' }}>
          <LoginForm onClose={() => setShowLoginForm(false)} />
        </div>
      )}

      {/* User Profile Modal */}
      {showUserProfile && (
        <div style={{ zIndex: 1100, position: 'relative' }}>
          <UserProfile user={user} onClose={() => setShowUserProfile(false)} />
        </div>
      )}
    </>
  );
};

// Hero Section with rotating images
const CuriHero = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % CURI_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Images with Crossfade */}
      {CURI_IMAGES.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentImageIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={image.url}
            alt={image.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-black/50" />
        </div>
      ))}

      {/* Hero Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.4)' }}>
          Curi the Kitten
        </h1>
        <p className="text-xl md:text-2xl text-white/90 mb-2" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.3)' }}>
          Follow Curi's Adventures Around the World!
        </p>
        <p className="text-lg text-orange-200 mb-8 max-w-2xl mx-auto" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
          A curious, fluffy explorer who sends messages home about his amazing adventures
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/store?category=subscriptions')}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
          >
            <PawPrint className="w-5 h-5 mr-2" />
            Get Your Curi Plush
          </Button>
          <Button
            onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white/20"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Read the Story
          </Button>
        </div>

        {/* Image indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {CURI_IMAGES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/70 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

// Story Section - About Curi
const StorySection = () => {
  return (
    <section id="story" className="py-20 bg-gradient-to-b from-amber-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-800 mb-4">
            Meet Curi the Kitten
          </h2>
          <p className="text-lg text-amber-700 max-w-2xl mx-auto">
            A heartwarming tale of curiosity, friendship, and adventure
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
          {/* Story Image */}
          <div className="relative">
            <img
              src="/curi-images/PhotoBasedArtwork_0010.jpg"
              alt="Curi and Julia"
              className="rounded-2xl shadow-2xl w-full"
            />
            <div className="absolute -bottom-4 -right-4 bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg">
              <span className="text-sm font-medium">Best Friends Forever</span>
            </div>
          </div>

          {/* Story Text */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-amber-500">
              <p className="text-gray-700 leading-relaxed">
                Once upon a time, in a cozy little house by the green meadows, lived a fluffy brown and white kitten named <strong>Curi</strong>. He wasn't just any kitten; he was found by a kind girl named <strong>Julia</strong> outside a candy store, and from that day, he had a new home and a new friend.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-orange-500">
              <p className="text-gray-700 leading-relaxed">
                Curi loves to chase bugs and birds, but most of all, he loves to <strong>explore new places</strong>. With a magical necklace that lets him send messages home, Curi embarks on grand adventures around the world, from the sunny beaches of California to the bustling streets of faraway cities.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-rose-500">
              <p className="text-gray-700 leading-relaxed">
                Every message from Curi is like a treasure chest, filled with stories of new lands and friendly faces. Julia's world grows with each tale, her imagination painting pictures of the places Curi roams.
              </p>
            </div>
          </div>
        </div>

        {/* Character Gallery */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-amber-800 text-center mb-8">Curi's Friends</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <img src="/curi-images/Upscale_0047.jpg" alt="Curi" className="w-32 h-32 mx-auto rounded-full object-cover shadow-lg border-4 border-amber-300" />
              <p className="mt-2 font-medium text-amber-800">Curi</p>
              <p className="text-sm text-amber-600">The Explorer</p>
            </div>
            <div className="text-center">
              <img src="/curi-images/PhotoBasedArtwork_0013.jpg" alt="Julia" className="w-32 h-32 mx-auto rounded-full object-cover shadow-lg border-4 border-orange-300" />
              <p className="mt-2 font-medium text-amber-800">Julia</p>
              <p className="text-sm text-amber-600">Best Friend</p>
            </div>
            <div className="text-center">
              <img src="/curi-images/Upscale_0174.jpg" alt="Smoky" className="w-32 h-32 mx-auto rounded-full object-cover shadow-lg border-4 border-rose-300" />
              <p className="mt-2 font-medium text-amber-800">Smoky</p>
              <p className="text-sm text-amber-600">Playful Puppy</p>
            </div>
            <div className="text-center">
              <img src="/curi-images/Upscale_0257.jpg" alt="Audi" className="w-32 h-32 mx-auto rounded-full object-cover shadow-lg border-4 border-yellow-300" />
              <p className="mt-2 font-medium text-amber-800">Audi</p>
              <p className="text-sm text-amber-600">California Cat</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      icon: <PawPrint className="w-8 h-8" />,
      title: "Get Your Curi",
      description: "Order a Curi plush with a special QR tag that connects to our adventure tracking system"
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Send Curi Exploring",
      description: "Let Curi travel with friends, family, or even strangers to explore new places"
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Messages Home",
      description: "Scan the QR code to share photos and stories from Curi's adventures"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Track the Journey",
      description: "Watch Curi's adventures unfold on the map as messages arrive from around the world"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-800 mb-4">
            How Curi's Adventures Work
          </h2>
          <p className="text-lg text-amber-700">
            Just like in the story, your Curi can send messages home!
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white shadow-lg">
                {step.icon}
              </div>
              <h3 className="text-xl font-bold text-amber-800 mb-2">{step.title}</h3>
              <p className="text-amber-700">{step.description}</p>
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2">
                  <ArrowRight className="w-6 h-6 text-amber-300" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Curi Club Page Component
const CuriKittenClubPage = () => {
  const [searchParams] = useSearchParams();
  const selectedItemId = searchParams.get('item');

  // Theme colors for Curi
  const themeColors = {
    primary: '#f59e0b', // amber-500
    secondary: '#ea580c', // orange-600
    accent: '#fbbf24', // amber-400
    background: '#fffbeb', // amber-50
    text: '#92400e' // amber-800
  };

  // Curi theme for MapFilterProvider (matches JeepDuck pattern)
  const curiTheme = {
    primary: 'amber',
    secondary: 'orange',
    accent: 'amber',
    gradientFrom: 'amber-500',
    gradientTo: 'orange-500',
    gradientStyle: 'linear-gradient(to right, #f59e0b, #ea580c)', // amber-500 to orange-600
    primaryColor: '#f59e0b', // amber-500
    secondaryColor: '#ea580c', // orange-600
    iconColor: '#d97706', // amber-600
    text: 'amber',
    background: 'amber-50',
    border: 'amber-200'
  };

  return (
    <MapFilterProvider 
      initialItemTypeFilter="traveling" 
      initialSearchTerm="curi"
      isPrivateTagFilter={true}
      theme={curiTheme}
    >
      <div className="min-h-screen bg-amber-50">
        <CuriHeader 
          clubName="Curi the Kitten" 
          tagline="Follow Curi's Adventures Around the World!" 
        />
        
        <CuriHero />
        
        <StorySection />
        
        {/* Flipbook Section */}
        <section id="flipbook" className="py-12 md:py-20 bg-gradient-to-b from-orange-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-amber-800 mb-3">
                Read Curi's Story
              </h2>
              <p className="text-lg text-amber-700 mb-2">
                Flip through the pages of Curi's adventures
              </p>
              <p className="text-sm text-amber-600">Click pages or use arrows to turn</p>
            </div>
            <StoryFlipbook themeColor="#f59e0b" />
          </div>
        </section>
        
        <HowItWorksSection />

        {/* Map Section */}
        <section id="map" className="py-8 md:py-16 bg-gradient-to-b from-white to-amber-50">
          <div className="container mx-auto px-2 sm:px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-amber-800 mb-4">
                Curi's Adventure Map
              </h2>
              <p className="text-lg text-amber-700">
                See where Curi and friends are exploring around the world
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-amber-200">
              <InteractiveMap 
                isClubPage={true} 
                clubTheme="curi"
                privateTagFilter="curi"
              />
            </div>
          </div>
        </section>

        {/* Stories/Messages Section */}
        <section id="stories" className="py-8 md:py-16 bg-amber-50">
          <div className="container mx-auto px-2 sm:px-4">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-amber-800 mb-4">
                Messages from Curi
              </h2>
              <p className="text-lg text-amber-700">
                Stories and photos from Curi's adventures around the world
              </p>
            </div>
            <div className="max-w-6xl mx-auto">
              <FeaturedStories 
                filter="traveling"
                privateTagFilter="curi"
                showSearch={false}
                maxStories={12}
              />
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Start Your Own Curi Adventure!
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Get your own Curi plush with a magic travel tag and watch the adventures unfold
            </p>
            <Button
              onClick={() => window.location.href = '/store?theme=curi'}
              size="lg"
              className="bg-white text-orange-600 hover:bg-orange-50 shadow-lg"
            >
              <PawPrint className="w-5 h-5 mr-2" />
              Order Your Curi Today
            </Button>
          </div>
        </section>

        <ClubShowcase excludeClubId="curi" title="Explore More Communities" />
        <Footer />
      </div>
    </MapFilterProvider>
  );
};

export default CuriKittenClubPage;
